import {
  aiFirstPrinciples,
  aiPipelineEvidence,
  pillarEntries,
  portfolioProfile,
  workflowStages,
} from "../content/portfolio";
import { navigate } from "../lib/navigation";
import { ContactActions } from "./ContactActions";

const readingSections = [
  { id: "read-about", label: "About" },
  { id: "read-ai", label: "AI-first engineering" },
  ...pillarEntries.map((pillar) => ({
    id: `read-${pillar.id}`,
    label: pillar.label,
  })),
  { id: "read-editions", label: "Machine-readable" },
  { id: "read-contact", label: "Contact" },
];

function focusSection(id: string) {
  const section = document.getElementById(id);
  section?.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "auto"
      : "smooth",
    block: "start",
  });
  section?.focus({ preventScroll: true });
}

export function ReadingView() {
  return (
    <main id="main-content" className="reading-view" tabIndex={-1}>
      <div className="reading-page">
        <header className="reading-hero">
          <div>
            <p className="signal-label">Complete portfolio · text edition</p>
            <h1>
              {portfolioProfile.home.headlineLead}{" "}
              <span>{portfolioProfile.home.headlineEmphasis}</span>
            </h1>
            <p>{portfolioProfile.home.introduction}</p>
            <div className="reading-actions">
              <button type="button" onClick={() => navigate("/")}>
                Enter the interactive world
                <span aria-hidden="true">↗</span>
              </button>
              <a
                href={portfolioProfile.resumeUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open resume
                <span aria-hidden="true">↗</span>
              </a>
              <a href="/portfolio.md" type="text/markdown">
                Open as Markdown
              </a>
            </div>
          </div>
          <aside className="reading-edition-note">
            <span>Two interfaces. One source.</span>
            <p>
              This reading view loads without WebGL and presents the same
              portfolio as a linear, keyboard-friendly document.
            </p>
            <a href="/llms.txt">LLM discovery file</a>
          </aside>
        </header>

        <div className="reading-layout">
          <nav className="reading-toc" aria-label="Reading view contents">
            <p>On this page</p>
            <ol>
              {readingSections.map((section) => (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => focusSection(section.id)}
                  >
                    {section.label}
                  </button>
                </li>
              ))}
            </ol>
          </nav>

          <article className="reading-content">
            <section
              id="read-about"
              className="reading-section"
              tabIndex={-1}
            >
              <p className="reading-kicker">{portfolioProfile.about.signal}</p>
              <h2>{portfolioProfile.about.title}</h2>
              <p className="reading-lede">{portfolioProfile.about.summary}</p>
              <dl className="reading-facts">
                {portfolioProfile.about.facts.map((fact) => (
                  <div key={fact.label}>
                    <dt>{fact.label}</dt>
                    <dd>{fact.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="reading-themes">
                {portfolioProfile.about.themes.map((theme) => (
                  <section key={theme.label}>
                    <h3>{theme.label}</h3>
                    <p>{theme.description}</p>
                  </section>
                ))}
              </div>
            </section>

            <section id="read-ai" className="reading-section" tabIndex={-1}>
              <p className="reading-kicker">Engineering philosophy</p>
              <h2>{portfolioProfile.aiFirst.title}</h2>
              <p className="reading-lede">{portfolioProfile.aiFirst.summary}</p>
              <div className="reading-principles">
                {aiFirstPrinciples.map((principle) => (
                  <section key={principle.id}>
                    <span>{principle.label}</span>
                    <h3>{principle.title}</h3>
                    <p>{principle.description}</p>
                  </section>
                ))}
              </div>
              <aside className="reading-evidence">
                <span>Proof system · autonomous PR pipeline</span>
                <h3>{aiPipelineEvidence.title}</h3>
                <p>{aiPipelineEvidence.description}</p>
                <details>
                  <summary>
                    Read the eight-stage pipeline
                    <span>{workflowStages.length} stages</span>
                  </summary>
                  <div className="reading-stages">
                    {workflowStages.map((stage) => (
                      <section key={stage.id}>
                        <span>
                          {stage.label} · {stage.role}
                        </span>
                        <h4>{stage.title}</h4>
                        <p>{stage.description}</p>
                        <dl>
                          <div>
                            <dt>Autonomy</dt>
                            <dd>{stage.autonomy}</dd>
                          </div>
                          <div>
                            <dt>Quality gate</dt>
                            <dd>{stage.qualityGate}</dd>
                          </div>
                          <div>
                            <dt>Human checkpoint</dt>
                            <dd>{stage.humanCheckpoint}</dd>
                          </div>
                          <div>
                            <dt>Output</dt>
                            <dd>{stage.artifact}</dd>
                          </div>
                        </dl>
                      </section>
                    ))}
                  </div>
                </details>
              </aside>
            </section>

            {pillarEntries.map((pillar) => (
              <section
                id={`read-${pillar.id}`}
                className="reading-section"
                key={pillar.id}
                tabIndex={-1}
              >
                <p className="reading-kicker">{pillar.label}</p>
                <h2>{pillar.title}</h2>
                <p className="reading-lede">{pillar.summary}</p>
                <div className="reading-principles">
                  {pillar.principles.map((principle) => (
                    <section key={principle.id}>
                      <span>{principle.label}</span>
                      <h3>{principle.title}</h3>
                      <p>{principle.description}</p>
                    </section>
                  ))}
                </div>
                <aside className="reading-evidence" data-pillar={pillar.id}>
                  <span>Evidence</span>
                  <h3>{pillar.evidence.title}</h3>
                  <p>{pillar.evidence.summary}</p>
                  <dl className="reading-outcome">
                    <div>
                      <dt>Who it served</dt>
                      <dd>{pillar.evidence.audience}</dd>
                    </div>
                    <div>
                      <dt>Product decision</dt>
                      <dd>{pillar.evidence.decision}</dd>
                    </div>
                    <div>
                      <dt>What changed</dt>
                      <dd>{pillar.evidence.impact}</dd>
                    </div>
                  </dl>
                </aside>
              </section>
            ))}

            <section
              id="read-editions"
              className="reading-section reading-machine"
              tabIndex={-1}
            >
              <p className="reading-kicker">For people and agents</p>
              <h2>Use the edition that fits the job.</h2>
              <p className="reading-lede">
                Both machine-readable files are generated from the same source
                as this page whenever the site builds.
              </p>
              <div>
                <a href="/portfolio.md" type="text/markdown">
                  <span>Complete Markdown</span>
                  <strong>/portfolio.md</strong>
                  <p>
                    Every public philosophy, principle, evidence story, and
                    pipeline stage in one document.
                  </p>
                </a>
                <a href="/llms.txt">
                  <span>LLM discovery</span>
                  <strong>/llms.txt</strong>
                  <p>
                    A concise index that tells agents where to find the canonical
                    portfolio and its human editions.
                  </p>
                </a>
              </div>
            </section>

            <footer
              id="read-contact"
              className="reading-footer"
              tabIndex={-1}
            >
              <div className="reading-contact">
                <p>{portfolioProfile.contact.summary}</p>
                <ContactActions />
              </div>
              <button type="button" onClick={() => navigate("/")}>
                Return to the Curiosity Engine
              </button>
            </footer>
          </article>
        </div>
      </div>
    </main>
  );
}
